import { Controller } from "@hotwired/stimulus"
import { Spring, prefersReducedMotion } from "../lib/spring"
import { dismissed } from "../lib/feedback"

// The toast is a physical thing: it slides in on a spring, can be grabbed and
// thrown off the edge at any point, and if you let go short of the edge it
// settles back rather than snapping.
const DISMISS_FRACTION = 0.4 // of the toast's width
const DECELERATION = 0.998
const AUTO_DISMISS = 5000

export default class extends Controller {
  static targets = ["message"]

  connect() {
    const node = this.messageTarget
    this.width = node.offsetWidth || 320

    this.spring = new Spring({
      value: this.width * 1.2,
      response: 0.4,
      damping: 1,
      onUpdate: (value) => this.draw(value),
      onRest: () => { if (this.dismissing) node.remove() }
    })

    if (prefersReducedMotion()) {
      // No travel, but the toast still has to arrive — fade it in instead.
      node.style.transform = "translateX(0)"
      node.style.transition = "opacity 200ms ease"
      requestAnimationFrame(() => { node.style.opacity = "1" })
    } else {
      this.spring.to(0)
    }

    node.style.touchAction = "pan-y"
    this.onPointerDown = (event) => this.grab(event)
    node.addEventListener("pointerdown", this.onPointerDown)

    this.timeout = setTimeout(() => this.slideOut(), AUTO_DISMISS)
  }

  disconnect() {
    clearTimeout(this.timeout)
    if (this.spring) this.spring.stop()
    this.messageTarget.removeEventListener("pointerdown", this.onPointerDown)
  }

  draw(offset) {
    const node = this.messageTarget
    node.style.transform = `translate3d(${offset}px, 0, 0)`
    node.style.opacity = String(Math.max(0, 1 - (Math.abs(offset) / this.width)))
  }

  // --- grab ----------------------------------------------------------------

  grab(event) {
    if (event.target.closest("button")) return
    if (event.button !== undefined && event.button !== 0) return

    clearTimeout(this.timeout)
    // Take over from wherever the entrance animation currently is, rather than
    // finishing it first.
    this.spring.stop()

    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      origin: this.spring.value,
      lastX: event.clientX,
      lastTime: event.timeStamp,
      velocity: 0
    }

    this.messageTarget.setPointerCapture(event.pointerId)
    this.onMove = (moveEvent) => this.move(moveEvent)
    this.onUp = () => this.release()
    this.messageTarget.addEventListener("pointermove", this.onMove)
    this.messageTarget.addEventListener("pointerup", this.onUp)
    this.messageTarget.addEventListener("pointercancel", this.onUp)
  }

  move(event) {
    if (!this.drag) return

    const elapsed = event.timeStamp - this.drag.lastTime
    if (elapsed > 0) this.drag.velocity = ((event.clientX - this.drag.lastX) / elapsed) * 1000
    this.drag.lastX = event.clientX
    this.drag.lastTime = event.timeStamp

    // Dragging away from the edge it came from has nothing behind it, so resist.
    const raw = this.drag.origin + (event.clientX - this.drag.startX)
    this.spring.value = raw < 0 ? raw * 0.3 : raw
    this.draw(this.spring.value)
  }

  release() {
    const drag = this.drag
    this.stopTracking()
    if (!drag) return

    // Land where the throw is heading, not where the finger happened to stop.
    const projected = this.spring.value + this.project(drag.velocity)

    if (projected > this.width * DISMISS_FRACTION) {
      this.dismissing = true
      // The throw committed — confirm it on the same frame the toast turns to leave.
      dismissed()
      this.spring.to(this.width * 1.2, { velocity: drag.velocity })
      return
    }

    this.spring.to(0, { velocity: drag.velocity })
    this.timeout = setTimeout(() => this.slideOut(), AUTO_DISMISS)
  }

  // Scroll deceleration, the same curve a flick follows anywhere else.
  project(velocity) {
    return (velocity / 1000) * DECELERATION / (1 - DECELERATION)
  }

  stopTracking() {
    const node = this.messageTarget
    if (this.drag && node.hasPointerCapture(this.drag.pointerId)) {
      node.releasePointerCapture(this.drag.pointerId)
    }
    if (this.onMove) node.removeEventListener("pointermove", this.onMove)
    if (this.onUp) {
      node.removeEventListener("pointerup", this.onUp)
      node.removeEventListener("pointercancel", this.onUp)
    }
    this.onMove = null
    this.onUp = null
    this.drag = null
  }

  // --- dismissal -----------------------------------------------------------

  dismiss() {
    clearTimeout(this.timeout)
    this.slideOut()
  }

  slideOut() {
    if (this.drag) return

    this.dismissing = true

    if (prefersReducedMotion()) {
      this.messageTarget.style.opacity = "0"
      setTimeout(() => this.messageTarget.remove(), 200)
      return
    }

    // Leaves the way it arrived, so the toast keeps one home on screen.
    this.spring.to(this.width * 1.2)
  }
}
